import type {
  ContentHash,
  ContentRef,
  PublicationTargetProfileRef,
} from "@cogo/publisher-core";

export const COGO_OUTCOME_SKU_SCHEMA_VERSION = "cogo.outcome-sku.v1" as const;
export const COGO_CANDIDATE_LIFECYCLE_EVENT_SCHEMA_VERSION =
  "cogo.candidate-lifecycle-event.v1" as const;
export const COGO_EVIDENCE_CAPSULE_REF_CONTRACT_VERSION =
  "cogo.evidence-capsule-ref.v1" as const;
export const COGO_CANDIDATE_LIFECYCLE_JOURNAL_DATA_KEY =
  "candidateLifecycleEvent" as const;

/**
 * Canonical productization evidence reference. This is an alias of the
 * publisher-core ContentRef contract, not a second evidence mechanism.
 */
export type EvidenceCapsuleRef = ContentRef;

export type OutcomeSkuState =
  | "demand-candidate"
  | "preview-candidate"
  | "verified"
  | "published"
  | "retired";

export interface OutcomeSkuScenarioRef {
  scenarioId: string;
  title: string;
  given: string[];
  when: string[];
  then: string[];
}

export interface OutcomeSkuVerificationCapsuleBinding {
  scenarioIds: string[];
  capsuleRef: EvidenceCapsuleRef;
}

export interface OutcomeSkuCoverageContract {
  includedScenarios: OutcomeSkuScenarioRef[];
  excluded: string[];
  customizationBoundary: {
    ready: string[];
    tailored: string[];
    bespokeRequired: string[];
  };
  targets: PublicationTargetProfileRef[];
}

export interface OutcomeSkuEvidence {
  designPackRef?: EvidenceCapsuleRef;
  verificationCapsuleRefs: OutcomeSkuVerificationCapsuleBinding[];
  sufficiencyAssessmentRevision: ContentHash | null;
  behaviorParityProofRef?: EvidenceCapsuleRef;
  lastCanaryRunRef?: EvidenceCapsuleRef;
}

export interface OutcomeSkuLifecycleBinding {
  projectId: string;
  candidateId: string;
  journalRunId: string;
  aggregateVersion: number;
  lastTransitionId: string;
}

export type OutcomeSkuPricing =
  | {
      model: "ready-fixed";
      currency: string;
      amountMinor: number;
    }
  | {
      model: "tailored-scoped";
      currency: string;
      startingAtMinor: number;
      scopeUnit: string;
    }
  | {
      model: "bespoke-quote";
      quoteRequired: true;
    };

export interface OutcomeSku {
  schemaVersion: typeof COGO_OUTCOME_SKU_SCHEMA_VERSION;
  skuId: string;
  productFamily: string;
  edition?: string;
  /** Cached catalog state. Authoritative admission derives it from C-2. */
  state: OutcomeSkuState;
  lifecycleBinding: OutcomeSkuLifecycleBinding;
  coverageContract: OutcomeSkuCoverageContract;
  evidence: OutcomeSkuEvidence;
  provenance: {
    ontologyReleaseId: string;
    rightsLedgerRefs: EvidenceCapsuleRef[];
  };
  pricing: OutcomeSkuPricing;
  demand: {
    searchHits: number;
    previewStarts: number;
    customizeStarts: number;
    purchases: number;
  };
}

export type CandidateLifecycleState = OutcomeSkuState | "customer-instance";

export type CandidateLifecycleTransition =
  | "record-demand"
  | "plan"
  | "verify"
  | "approve"
  | "instantiate"
  | "request-revision"
  | "retire";

export type CandidateLifecycleActorAuthority =
  | "human"
  | "studio"
  | "generation"
  | "publisher"
  | "runtime";

export type CandidateLifecycleEvidenceRole =
  | "demand-signal"
  | "preview-plan"
  | "sufficiency-assessment"
  | "validator-safety-pass"
  | "second-model-judge"
  | "render-execution-receipt"
  | "verification-capsule"
  | "human-approval"
  | "publication-receipt"
  | "customer-instance-receipt"
  | "revision-request"
  | "retirement-decision";

export interface CandidateLifecycleEvidenceBinding {
  role: CandidateLifecycleEvidenceRole;
  ref: EvidenceCapsuleRef;
}

export interface ParentSkuLineageRef {
  skuId: string;
  publicationReleaseId: string;
  parentTransitionId: string;
}

export interface CandidateLifecycleEvent {
  schemaVersion: typeof COGO_CANDIDATE_LIFECYCLE_EVENT_SCHEMA_VERSION;
  transitionId: string;
  journalRunId: string;
  aggregateVersion: number;
  previousTransitionId: string | null;
  skuId: string;
  candidateId: string;
  projectId: string;
  fromState: CandidateLifecycleState | null;
  toState: CandidateLifecycleState;
  transition: CandidateLifecycleTransition;
  occurredAt: string;
  actor: {
    authority: CandidateLifecycleActorAuthority;
    actorRef: string;
  };
  evidenceRefs: CandidateLifecycleEvidenceBinding[];
  reasonCode?: string;
  customerInstanceId?: string;
  parentSkuLineage?: ParentSkuLineageRef;
}

export interface CandidateLifecycleProjection {
  skuId: string;
  candidateId: string;
  projectId: string;
  journalRunId: string;
  state: CandidateLifecycleState;
  aggregateVersion: number;
  customerInstanceId?: string;
  lastTransitionId: string;
  updatedAt: string;
  history: CandidateLifecycleEvent[];
}

export interface ProductizationContractValidationResult {
  valid: boolean;
  errors: string[];
}

const SHA256_PATTERN = /^sha256:[a-f0-9]{64}$/;
const ISO_TIMESTAMP_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;

const OUTCOME_SKU_STATES = new Set<OutcomeSkuState>([
  "demand-candidate",
  "preview-candidate",
  "verified",
  "published",
  "retired",
]);
const CANDIDATE_LIFECYCLE_STATES = new Set<CandidateLifecycleState>([
  ...OUTCOME_SKU_STATES,
  "customer-instance",
]);
const ACTOR_AUTHORITIES = new Set<CandidateLifecycleActorAuthority>([
  "human",
  "studio",
  "generation",
  "publisher",
  "runtime",
]);
const EVIDENCE_ROLES = new Set<CandidateLifecycleEvidenceRole>([
  "demand-signal",
  "preview-plan",
  "sufficiency-assessment",
  "validator-safety-pass",
  "second-model-judge",
  "render-execution-receipt",
  "verification-capsule",
  "human-approval",
  "publication-receipt",
  "customer-instance-receipt",
  "revision-request",
  "retirement-decision",
]);

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function exactLineageKeys(value: Record<string, unknown>): boolean {
  return (
    Object.keys(value).sort().join("\0") ===
    ["parentTransitionId", "publicationReleaseId", "skuId"].join("\0")
  );
}

function rejectUnexpectedKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
  label: string,
  errors: string[],
): void {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key));
  if (unexpected.length > 0) {
    errors.push(
      `${label} contains unsupported fields: ${unexpected.sort().join(",")}`,
    );
  }
}

function uniqueNonEmptyStrings(
  value: unknown,
  label: string,
  errors: string[],
  options: { requireOne?: boolean } = {},
): string[] {
  if (!Array.isArray(value)) {
    errors.push(`${label} must be an array`);
    return [];
  }
  const entries = value.filter(nonEmptyString);
  if (entries.length !== value.length) {
    errors.push(`${label} must contain only non-empty strings`);
  }
  if (options.requireOne && entries.length === 0) {
    errors.push(`${label} must contain at least one item`);
  }
  if (new Set(entries).size !== entries.length) {
    errors.push(`${label} must not contain duplicates`);
  }
  return entries;
}

function validateEvidenceRefInto(
  value: unknown,
  label: string,
  errors: string[],
): value is EvidenceCapsuleRef {
  const initialErrorCount = errors.length;
  if (!isRecord(value)) {
    errors.push(`${label} must be an object`);
    return false;
  }
  const keys = Object.keys(value);
  if (
    keys.length !== 2 ||
    !Object.hasOwn(value, "ref") ||
    !Object.hasOwn(value, "contentHash")
  ) {
    errors.push(`${label} must contain only ref and contentHash`);
  }
  if (!nonEmptyString(value.ref)) errors.push(`${label}.ref is required`);
  if (
    typeof value.contentHash !== "string" ||
    !SHA256_PATTERN.test(value.contentHash)
  ) {
    errors.push(`${label}.contentHash must be a canonical sha256 hash`);
  }
  return errors.length === initialErrorCount;
}

function validateEvidenceRefArray(
  value: unknown,
  label: string,
  errors: string[],
  options: { requireOne?: boolean } = {},
): EvidenceCapsuleRef[] {
  if (!Array.isArray(value)) {
    errors.push(`${label} must be an array`);
    return [];
  }
  if (options.requireOne && value.length === 0) {
    errors.push(`${label} must contain at least one item`);
  }
  const refs: EvidenceCapsuleRef[] = [];
  value.forEach((entry, index) => {
    const before = errors.length;
    validateEvidenceRefInto(entry, `${label}[${index}]`, errors);
    if (errors.length === before) refs.push(entry as EvidenceCapsuleRef);
  });
  const identities = refs.map((entry) => entry.ref);
  if (new Set(identities).size !== identities.length) {
    errors.push(`${label} must not contain duplicate content references`);
  }
  return refs;
}

function validateCandidateEvidenceBindings(
  value: unknown,
  errors: string[],
): CandidateLifecycleEvidenceBinding[] {
  if (!Array.isArray(value)) {
    errors.push("evidenceRefs must be an array");
    return [];
  }
  const bindings: CandidateLifecycleEvidenceBinding[] = [];
  value.forEach((entry, index) => {
    const label = `evidenceRefs[${index}]`;
    if (!isRecord(entry)) {
      errors.push(`${label} must be an object`);
      return;
    }
    rejectUnexpectedKeys(entry, ["role", "ref"], label, errors);
    if (!EVIDENCE_ROLES.has(entry.role as CandidateLifecycleEvidenceRole)) {
      errors.push(`${label}.role is invalid`);
    }
    const before = errors.length;
    validateEvidenceRefInto(entry.ref, `${label}.ref`, errors);
    if (errors.length === before) {
      bindings.push(entry as unknown as CandidateLifecycleEvidenceBinding);
    }
  });
  const identities = bindings.map(
    (binding) => `${binding.role}\0${binding.ref.ref}`,
  );
  if (new Set(identities).size !== identities.length) {
    errors.push(
      "evidenceRefs must not contain duplicate role and ref bindings",
    );
  }
  const contentIdentities = bindings.map((binding) => binding.ref.contentHash);
  if (new Set(contentIdentities).size !== contentIdentities.length) {
    errors.push(
      "evidenceRefs must use distinct content references for distinct roles",
    );
  }
  return bindings;
}

function validateCandidateEvidenceResourceKinds(
  bindings: CandidateLifecycleEvidenceBinding[],
  projectId: unknown,
  errors: string[],
): void {
  for (const binding of bindings) {
    const { ref, contentHash } = binding.ref;
    if (
      binding.role === "demand-signal" &&
      !["demand-signal:", "telemetry:"].some((prefix) => ref.startsWith(prefix))
    ) {
      errors.push(
        "demand-signal evidence must use a demand-signal or telemetry reference",
      );
    }
    if (
      binding.role === "preview-plan" &&
      !["preview-plan:", "orchestration-plan:"].some((prefix) =>
        ref.startsWith(prefix),
      )
    ) {
      errors.push(
        "preview-plan evidence must use a preview-plan or orchestration-plan reference",
      );
    }
    if (binding.role === "sufficiency-assessment") {
      const sourceAssessmentPrefix = `source-assessment:${String(projectId)}:`;
      if (
        ref !== contentHash &&
        !(ref.startsWith(sourceAssessmentPrefix) && ref.endsWith(contentHash))
      ) {
        errors.push(
          "sufficiency-assessment evidence must reference the exact assessment revision",
        );
      }
    }
    if (
      binding.role === "verification-capsule" &&
      ![
        "verification-capsule:",
        "verification-run:",
        "verification-evidence-envelope:",
      ].some((prefix) => ref.startsWith(prefix))
    ) {
      errors.push(
        "verification-capsule evidence must use a publishing-kernel verification reference",
      );
    }
    for (const [role, prefix] of [
      ["validator-safety-pass", "validator-safety-pass:"],
      ["second-model-judge", "second-model-judge:"],
    ] as const) {
      if (binding.role === role && !ref.startsWith(prefix)) {
        errors.push(`${role} evidence must use a ${prefix} reference`);
      }
    }
    if (
      binding.role === "render-execution-receipt" &&
      !["render-execution-receipt:", "verification-evidence-envelope:"].some(
        (prefix) => ref.startsWith(prefix),
      )
    ) {
      errors.push(
        "render-execution-receipt evidence must use a render or verification envelope reference",
      );
    }
    if (
      binding.role === "retirement-decision" &&
      !ref.startsWith("retirement-decision:")
    ) {
      errors.push(
        "retirement-decision evidence must use a retirement-decision reference",
      );
    }
    if (
      ["publication-receipt", "customer-instance-receipt"].includes(
        binding.role,
      ) &&
      !ref.startsWith("delivery-receipt:")
    ) {
      errors.push(
        `${binding.role} evidence must use a publishing-kernel delivery receipt reference`,
      );
    }
  }
}

export function validateEvidenceCapsuleRef(
  value: unknown,
): ProductizationContractValidationResult {
  const errors: string[] = [];
  validateEvidenceRefInto(value, "evidenceCapsuleRef", errors);
  return { valid: errors.length === 0, errors };
}

export function assertEvidenceCapsuleRef(
  value: unknown,
): asserts value is EvidenceCapsuleRef {
  const validation = validateEvidenceCapsuleRef(value);
  if (!validation.valid) {
    throw new Error(
      `INVALID_EVIDENCE_CAPSULE_REF:${validation.errors.join(";")}`,
    );
  }
}

function validateScenario(
  value: unknown,
  index: number,
  errors: string[],
): OutcomeSkuScenarioRef | null {
  const label = `coverageContract.includedScenarios[${index}]`;
  if (!isRecord(value)) {
    errors.push(`${label} must be an object`);
    return null;
  }
  rejectUnexpectedKeys(
    value,
    ["scenarioId", "title", "given", "when", "then"],
    label,
    errors,
  );
  if (!nonEmptyString(value.scenarioId))
    errors.push(`${label}.scenarioId is required`);
  if (!nonEmptyString(value.title)) errors.push(`${label}.title is required`);
  uniqueNonEmptyStrings(value.given, `${label}.given`, errors, {
    requireOne: true,
  });
  uniqueNonEmptyStrings(value.when, `${label}.when`, errors, {
    requireOne: true,
  });
  uniqueNonEmptyStrings(value.then, `${label}.then`, errors, {
    requireOne: true,
  });
  return nonEmptyString(value.scenarioId)
    ? (value as unknown as OutcomeSkuScenarioRef)
    : null;
}

function validateTargetProfileRefs(
  value: unknown,
  errors: string[],
): PublicationTargetProfileRef[] {
  const label = "coverageContract.targets";
  if (!Array.isArray(value)) {
    errors.push(`${label} must be an array`);
    return [];
  }
  const refs: PublicationTargetProfileRef[] = [];
  value.forEach((entry, index) => {
    const itemLabel = `${label}[${index}]`;
    if (!isRecord(entry)) {
      errors.push(`${itemLabel} must be an object`);
      return;
    }
    rejectUnexpectedKeys(
      entry,
      ["profileId", "profileVersion", "contentHash"],
      itemLabel,
      errors,
    );
    if (!nonEmptyString(entry.profileId))
      errors.push(`${itemLabel}.profileId is required`);
    if (!nonEmptyString(entry.profileVersion))
      errors.push(`${itemLabel}.profileVersion is required`);
    if (
      typeof entry.contentHash !== "string" ||
      !SHA256_PATTERN.test(entry.contentHash)
    ) {
      errors.push(`${itemLabel}.contentHash must be a canonical sha256 hash`);
    }
    refs.push(entry as unknown as PublicationTargetProfileRef);
  });
  const identities = refs.map(
    (entry) => `${entry.profileId}@${entry.profileVersion}`,
  );
  if (new Set(identities).size !== identities.length) {
    errors.push(`${label} must not contain duplicate profiles`);
  }
  return refs;
}

function validatePricing(value: unknown, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push("pricing must be an object");
    return;
  }
  if (value.model === "ready-fixed") {
    rejectUnexpectedKeys(
      value,
      ["model", "currency", "amountMinor"],
      "pricing",
      errors,
    );
    if (
      typeof value.currency !== "string" ||
      !CURRENCY_PATTERN.test(value.currency)
    ) {
      errors.push("pricing.currency must be an ISO 4217-style uppercase code");
    }
    if (
      !Number.isSafeInteger(value.amountMinor) ||
      Number(value.amountMinor) < 0
    ) {
      errors.push("pricing.amountMinor must be a non-negative safe integer");
    }
    return;
  }
  if (value.model === "tailored-scoped") {
    rejectUnexpectedKeys(
      value,
      ["model", "currency", "startingAtMinor", "scopeUnit"],
      "pricing",
      errors,
    );
    if (
      typeof value.currency !== "string" ||
      !CURRENCY_PATTERN.test(value.currency)
    ) {
      errors.push("pricing.currency must be an ISO 4217-style uppercase code");
    }
    if (
      !Number.isSafeInteger(value.startingAtMinor) ||
      Number(value.startingAtMinor) < 0
    ) {
      errors.push(
        "pricing.startingAtMinor must be a non-negative safe integer",
      );
    }
    if (!nonEmptyString(value.scopeUnit))
      errors.push("pricing.scopeUnit is required");
    return;
  }
  if (value.model === "bespoke-quote") {
    rejectUnexpectedKeys(value, ["model", "quoteRequired"], "pricing", errors);
    if (value.quoteRequired !== true)
      errors.push("pricing.quoteRequired must be true");
    return;
  }
  errors.push("pricing.model is invalid");
}

export function validateOutcomeSku(
  value: unknown,
): ProductizationContractValidationResult {
  const errors: string[] = [];
  if (!isRecord(value))
    return { valid: false, errors: ["outcomeSku must be an object"] };
  rejectUnexpectedKeys(
    value,
    [
      "schemaVersion",
      "skuId",
      "productFamily",
      "edition",
      "state",
      "lifecycleBinding",
      "coverageContract",
      "evidence",
      "provenance",
      "pricing",
      "demand",
    ],
    "outcomeSku",
    errors,
  );
  if (value.schemaVersion !== COGO_OUTCOME_SKU_SCHEMA_VERSION) {
    errors.push(`schemaVersion must be ${COGO_OUTCOME_SKU_SCHEMA_VERSION}`);
  }
  for (const [label, candidate] of [
    ["skuId", value.skuId],
    ["productFamily", value.productFamily],
  ] as const) {
    if (!nonEmptyString(candidate)) errors.push(`${label} is required`);
  }
  if (value.edition !== undefined && !nonEmptyString(value.edition)) {
    errors.push("edition must be non-empty when provided");
  }
  if (!OUTCOME_SKU_STATES.has(value.state as OutcomeSkuState)) {
    errors.push("state is invalid");
  }
  const lifecycleBinding = isRecord(value.lifecycleBinding)
    ? value.lifecycleBinding
    : null;
  if (!lifecycleBinding) {
    errors.push("lifecycleBinding is required");
  } else {
    rejectUnexpectedKeys(
      lifecycleBinding,
      [
        "projectId",
        "candidateId",
        "journalRunId",
        "aggregateVersion",
        "lastTransitionId",
      ],
      "lifecycleBinding",
      errors,
    );
    for (const field of [
      "projectId",
      "candidateId",
      "journalRunId",
      "lastTransitionId",
    ] as const) {
      if (!nonEmptyString(lifecycleBinding[field])) {
        errors.push(`lifecycleBinding.${field} is required`);
      }
    }
    if (
      !Number.isSafeInteger(lifecycleBinding.aggregateVersion) ||
      Number(lifecycleBinding.aggregateVersion) < 1
    ) {
      errors.push(
        "lifecycleBinding.aggregateVersion must be a positive safe integer",
      );
    }
  }

  const coverage = isRecord(value.coverageContract)
    ? value.coverageContract
    : null;
  if (!coverage) errors.push("coverageContract is required");
  if (coverage) {
    rejectUnexpectedKeys(
      coverage,
      ["includedScenarios", "excluded", "customizationBoundary", "targets"],
      "coverageContract",
      errors,
    );
  }
  const scenarios = Array.isArray(coverage?.includedScenarios)
    ? coverage.includedScenarios
        .map((entry, index) => validateScenario(entry, index, errors))
        .filter((entry): entry is OutcomeSkuScenarioRef => entry !== null)
    : (errors.push("coverageContract.includedScenarios must be an array"), []);
  const scenarioIds = scenarios.map((entry) => entry.scenarioId);
  if (new Set(scenarioIds).size !== scenarioIds.length) {
    errors.push(
      "coverageContract.includedScenarios must have unique scenarioId values",
    );
  }
  uniqueNonEmptyStrings(
    coverage?.excluded,
    "coverageContract.excluded",
    errors,
  );
  const boundary = isRecord(coverage?.customizationBoundary)
    ? coverage.customizationBoundary
    : null;
  if (!boundary)
    errors.push("coverageContract.customizationBoundary is required");
  if (boundary) {
    rejectUnexpectedKeys(
      boundary,
      ["ready", "tailored", "bespokeRequired"],
      "coverageContract.customizationBoundary",
      errors,
    );
  }
  const ready = uniqueNonEmptyStrings(
    boundary?.ready,
    "coverageContract.customizationBoundary.ready",
    errors,
  );
  const tailored = uniqueNonEmptyStrings(
    boundary?.tailored,
    "coverageContract.customizationBoundary.tailored",
    errors,
  );
  const bespoke = uniqueNonEmptyStrings(
    boundary?.bespokeRequired,
    "coverageContract.customizationBoundary.bespokeRequired",
    errors,
  );
  const boundaryItems = [...ready, ...tailored, ...bespoke];
  if (new Set(boundaryItems).size !== boundaryItems.length) {
    errors.push(
      "customization boundary entries must belong to exactly one tier",
    );
  }
  const targets = validateTargetProfileRefs(coverage?.targets, errors);

  const evidence = isRecord(value.evidence) ? value.evidence : null;
  if (!evidence) errors.push("evidence is required");
  if (evidence) {
    rejectUnexpectedKeys(
      evidence,
      [
        "designPackRef",
        "verificationCapsuleRefs",
        "sufficiencyAssessmentRevision",
        "behaviorParityProofRef",
        "lastCanaryRunRef",
      ],
      "evidence",
      errors,
    );
  }
  for (const field of [
    "designPackRef",
    "behaviorParityProofRef",
    "lastCanaryRunRef",
  ] as const) {
    if (evidence?.[field] !== undefined) {
      validateEvidenceRefInto(evidence[field], `evidence.${field}`, errors);
    }
  }
  if (evidence && !("sufficiencyAssessmentRevision" in evidence)) {
    errors.push("evidence.sufficiencyAssessmentRevision is required");
  } else if (
    evidence?.sufficiencyAssessmentRevision !== null &&
    (typeof evidence?.sufficiencyAssessmentRevision !== "string" ||
      !SHA256_PATTERN.test(evidence.sufficiencyAssessmentRevision))
  ) {
    errors.push(
      "evidence.sufficiencyAssessmentRevision must be a canonical sha256 hash",
    );
  }

  const bindings = Array.isArray(evidence?.verificationCapsuleRefs)
    ? evidence.verificationCapsuleRefs
    : (errors.push("evidence.verificationCapsuleRefs must be an array"), []);
  const boundScenarioIds = new Set<string>();
  const bindingIdentities: string[] = [];
  bindings.forEach((binding, index) => {
    const label = `evidence.verificationCapsuleRefs[${index}]`;
    if (!isRecord(binding)) {
      errors.push(`${label} must be an object`);
      return;
    }
    rejectUnexpectedKeys(binding, ["scenarioIds", "capsuleRef"], label, errors);
    const ids = uniqueNonEmptyStrings(
      binding.scenarioIds,
      `${label}.scenarioIds`,
      errors,
      { requireOne: true },
    );
    ids.forEach((scenarioId) => {
      if (!scenarioIds.includes(scenarioId)) {
        errors.push(
          `${label} references excluded or unknown scenario ${scenarioId}`,
        );
      } else {
        boundScenarioIds.add(scenarioId);
      }
    });
    const before = errors.length;
    validateEvidenceRefInto(binding.capsuleRef, `${label}.capsuleRef`, errors);
    if (errors.length === before) {
      const ref = binding.capsuleRef as unknown as EvidenceCapsuleRef;
      bindingIdentities.push(`${[...ids].sort().join(",")}\0${ref.ref}`);
    }
  });
  if (new Set(bindingIdentities).size !== bindingIdentities.length) {
    errors.push(
      "evidence.verificationCapsuleRefs must not contain duplicate bindings",
    );
  }

  const provenance = isRecord(value.provenance) ? value.provenance : null;
  if (!provenance) errors.push("provenance is required");
  if (provenance) {
    rejectUnexpectedKeys(
      provenance,
      ["ontologyReleaseId", "rightsLedgerRefs"],
      "provenance",
      errors,
    );
  }
  if (!nonEmptyString(provenance?.ontologyReleaseId)) {
    errors.push("provenance.ontologyReleaseId is required");
  }
  validateEvidenceRefArray(
    provenance?.rightsLedgerRefs,
    "provenance.rightsLedgerRefs",
    errors,
  );
  validatePricing(value.pricing, errors);

  const demand = isRecord(value.demand) ? value.demand : null;
  if (!demand) errors.push("demand is required");
  if (demand) {
    rejectUnexpectedKeys(
      demand,
      ["searchHits", "previewStarts", "customizeStarts", "purchases"],
      "demand",
      errors,
    );
  }
  for (const field of [
    "searchHits",
    "previewStarts",
    "customizeStarts",
    "purchases",
  ] as const) {
    if (!Number.isSafeInteger(demand?.[field]) || Number(demand?.[field]) < 0) {
      errors.push(`demand.${field} must be a non-negative safe integer`);
    }
  }

  if (value.state === "published") {
    if (scenarioIds.length === 0) {
      errors.push("published SKUs require at least one included scenario");
    }
    if (targets.length === 0) {
      errors.push("published SKUs require at least one target profile");
    }
    if (!evidence?.sufficiencyAssessmentRevision) {
      errors.push("published SKUs require a sufficiency assessment revision");
    }
    if (
      !Array.isArray(provenance?.rightsLedgerRefs) ||
      provenance.rightsLedgerRefs.length === 0
    ) {
      errors.push("published SKUs require rights ledger evidence");
    }
    for (const scenarioId of scenarioIds) {
      if (!boundScenarioIds.has(scenarioId)) {
        errors.push(
          `published SKU scenario ${scenarioId} requires a verification capsule`,
        );
      }
    }
  }
  if (value.state === "verified" && !evidence?.sufficiencyAssessmentRevision) {
    errors.push("verified SKUs require a sufficiency assessment revision");
  }

  return { valid: errors.length === 0, errors };
}

export function assertOutcomeSku(value: unknown): asserts value is OutcomeSku {
  const validation = validateOutcomeSku(value);
  if (!validation.valid) {
    throw new Error(`INVALID_OUTCOME_SKU:${validation.errors.join(";")}`);
  }
}

const TRANSITION_RULES: Record<
  CandidateLifecycleTransition,
  Array<{ from: CandidateLifecycleState | null; to: CandidateLifecycleState }>
> = {
  "record-demand": [{ from: null, to: "demand-candidate" }],
  plan: [{ from: "demand-candidate", to: "preview-candidate" }],
  verify: [{ from: "preview-candidate", to: "verified" }],
  approve: [{ from: "verified", to: "published" }],
  instantiate: [{ from: "published", to: "customer-instance" }],
  "request-revision": [{ from: "verified", to: "preview-candidate" }],
  retire: [
    { from: "demand-candidate", to: "retired" },
    { from: "preview-candidate", to: "retired" },
    { from: "verified", to: "retired" },
    { from: "published", to: "retired" },
  ],
};

function isAllowedTransition(
  transition: CandidateLifecycleTransition,
  fromState: CandidateLifecycleState | null,
  toState: CandidateLifecycleState,
): boolean {
  return TRANSITION_RULES[transition]?.some(
    (rule) => rule.from === fromState && rule.to === toState,
  );
}

export function validateCandidateLifecycleEvent(
  value: unknown,
): ProductizationContractValidationResult {
  const errors: string[] = [];
  if (!isRecord(value)) {
    return {
      valid: false,
      errors: ["candidateLifecycleEvent must be an object"],
    };
  }
  rejectUnexpectedKeys(
    value,
    [
      "schemaVersion",
      "transitionId",
      "journalRunId",
      "aggregateVersion",
      "previousTransitionId",
      "skuId",
      "candidateId",
      "projectId",
      "fromState",
      "toState",
      "transition",
      "occurredAt",
      "actor",
      "evidenceRefs",
      "reasonCode",
      "customerInstanceId",
      "parentSkuLineage",
    ],
    "candidateLifecycleEvent",
    errors,
  );
  if (value.schemaVersion !== COGO_CANDIDATE_LIFECYCLE_EVENT_SCHEMA_VERSION) {
    errors.push(
      `schemaVersion must be ${COGO_CANDIDATE_LIFECYCLE_EVENT_SCHEMA_VERSION}`,
    );
  }
  for (const [label, candidate] of [
    ["transitionId", value.transitionId],
    ["journalRunId", value.journalRunId],
    ["skuId", value.skuId],
    ["candidateId", value.candidateId],
    ["projectId", value.projectId],
    ["occurredAt", value.occurredAt],
  ] as const) {
    if (!nonEmptyString(candidate)) errors.push(`${label} is required`);
  }
  if (
    !Number.isSafeInteger(value.aggregateVersion) ||
    Number(value.aggregateVersion) < 1
  ) {
    errors.push("aggregateVersion must be a positive safe integer");
  }
  if (
    value.previousTransitionId !== null &&
    !nonEmptyString(value.previousTransitionId)
  ) {
    errors.push("previousTransitionId must be null or a non-empty string");
  }
  if (
    typeof value.occurredAt !== "string" ||
    !ISO_TIMESTAMP_PATTERN.test(value.occurredAt) ||
    Number.isNaN(Date.parse(value.occurredAt))
  ) {
    errors.push("occurredAt must be a valid ISO timestamp");
  }
  const fromState = value.fromState as CandidateLifecycleState | null;
  const toState = value.toState as CandidateLifecycleState;
  const transition = value.transition as CandidateLifecycleTransition;
  if (fromState !== null && !CANDIDATE_LIFECYCLE_STATES.has(fromState)) {
    errors.push("fromState is invalid");
  }
  if (!CANDIDATE_LIFECYCLE_STATES.has(toState)) {
    errors.push("toState is invalid");
  }
  if (!Object.hasOwn(TRANSITION_RULES, transition)) {
    errors.push("transition is invalid");
  } else if (
    (fromState === null || CANDIDATE_LIFECYCLE_STATES.has(fromState)) &&
    CANDIDATE_LIFECYCLE_STATES.has(toState) &&
    !isAllowedTransition(transition, fromState, toState)
  ) {
    errors.push(
      `transition ${transition} cannot move ${fromState ?? "null"} to ${toState}`,
    );
  }

  const actor = isRecord(value.actor) ? value.actor : null;
  if (!actor) errors.push("actor is required");
  if (actor) {
    rejectUnexpectedKeys(actor, ["authority", "actorRef"], "actor", errors);
  }
  if (
    !ACTOR_AUTHORITIES.has(actor?.authority as CandidateLifecycleActorAuthority)
  ) {
    errors.push("actor.authority is invalid");
  }
  if (!nonEmptyString(actor?.actorRef))
    errors.push("actor.actorRef is required");
  if (transition === "approve" && actor?.authority !== "human") {
    errors.push("approve transitions require human authority");
  }
  if (transition === "retire" && actor?.authority !== "human") {
    errors.push("retire transitions require human authority");
  }

  const evidenceBindings = validateCandidateEvidenceBindings(
    value.evidenceRefs,
    errors,
  );
  validateCandidateEvidenceResourceKinds(
    evidenceBindings,
    value.projectId,
    errors,
  );
  const evidenceRoles = new Set(
    evidenceBindings.map((binding) => binding.role),
  );
  const requiredRoles: Partial<
    Record<CandidateLifecycleTransition, CandidateLifecycleEvidenceRole[]>
  > = {
    "record-demand": ["demand-signal"],
    plan: ["preview-plan"],
    approve: ["human-approval"],
    verify: [
      "sufficiency-assessment",
      "validator-safety-pass",
      "second-model-judge",
      "render-execution-receipt",
      "verification-capsule",
    ],
    instantiate: ["publication-receipt", "customer-instance-receipt"],
    retire: ["retirement-decision"],
  };
  for (const role of requiredRoles[transition] ?? []) {
    if (!evidenceRoles.has(role)) {
      errors.push(`${transition} transitions require ${role} evidence`);
    }
  }
  const parentSkuLineage = isRecord(value.parentSkuLineage)
    ? value.parentSkuLineage
    : null;
  if (value.parentSkuLineage !== undefined) {
    if (transition !== "instantiate") {
      errors.push("parentSkuLineage is only allowed for instantiate transitions");
    } else if (
      !parentSkuLineage ||
      !exactLineageKeys(parentSkuLineage) ||
      !nonEmptyString(parentSkuLineage.skuId) ||
      !nonEmptyString(parentSkuLineage.publicationReleaseId) ||
      !nonEmptyString(parentSkuLineage.parentTransitionId)
    ) {
      errors.push(
        "parentSkuLineage must carry skuId, publicationReleaseId, parentTransitionId",
      );
    } else if (parentSkuLineage.skuId !== value.skuId) {
      errors.push("parentSkuLineage.skuId must match the event skuId");
    }
  }
  const isForkFirstInstantiate =
    transition === "instantiate" && value.parentSkuLineage !== undefined;
  if (transition === "record-demand") {
    if (value.aggregateVersion !== 1 || value.previousTransitionId !== null) {
      errors.push(
        "record-demand must use aggregateVersion=1 and previousTransitionId=null",
      );
    }
  } else if (isForkFirstInstantiate) {
    if (value.aggregateVersion !== 1 || value.previousTransitionId !== null) {
      errors.push(
        "fork-first instantiate must use aggregateVersion=1 and previousTransitionId=null",
      );
    }
  } else {
    if (Number(value.aggregateVersion) < 2) {
      errors.push(`${transition} transitions require aggregateVersion >= 2`);
    }
    if (!nonEmptyString(value.previousTransitionId)) {
      errors.push(`${transition} transitions require previousTransitionId`);
    }
  }
  if (value.reasonCode !== undefined && !nonEmptyString(value.reasonCode)) {
    errors.push("reasonCode must be non-empty when provided");
  }
  if (transition === "instantiate") {
    if (!nonEmptyString(value.customerInstanceId)) {
      errors.push("instantiate transitions require customerInstanceId");
    }
  } else if (value.customerInstanceId !== undefined) {
    errors.push(
      "customerInstanceId is only allowed for instantiate transitions",
    );
  }
  return { valid: errors.length === 0, errors };
}

export function assertCandidateLifecycleEvent(
  value: unknown,
): asserts value is CandidateLifecycleEvent {
  const validation = validateCandidateLifecycleEvent(value);
  if (!validation.valid) {
    throw new Error(
      `INVALID_CANDIDATE_LIFECYCLE_EVENT:${validation.errors.join(";")}`,
    );
  }
}

export function candidateLifecycleJournalData(
  event: CandidateLifecycleEvent,
): Record<
  typeof COGO_CANDIDATE_LIFECYCLE_JOURNAL_DATA_KEY,
  CandidateLifecycleEvent
> {
  assertCandidateLifecycleEvent(event);
  return { [COGO_CANDIDATE_LIFECYCLE_JOURNAL_DATA_KEY]: event };
}

export function candidateLifecycleEventFromJournalData(
  data: unknown,
): CandidateLifecycleEvent | null {
  if (!isRecord(data)) return null;
  const value = data[COGO_CANDIDATE_LIFECYCLE_JOURNAL_DATA_KEY];
  if (value === undefined) return null;
  assertCandidateLifecycleEvent(value);
  return value;
}

export function deriveCandidateLifecycleProjection(
  events: CandidateLifecycleEvent[],
): CandidateLifecycleProjection {
  if (events.length === 0) {
    throw new Error("CANDIDATE_LIFECYCLE_EVENTS_REQUIRED");
  }
  const orderedEvents = [...events].sort(
    (left, right) => left.aggregateVersion - right.aggregateVersion,
  );
  const history: CandidateLifecycleEvent[] = [];
  const transitionIds = new Set<string>();
  const aggregateVersions = new Set<number>();
  let state: CandidateLifecycleState | null = null;
  let first: CandidateLifecycleEvent | undefined;
  let customerInstanceId: string | undefined;
  for (const event of orderedEvents) {
    assertCandidateLifecycleEvent(event);
    first ??= event;
    if (
      event.skuId !== first.skuId ||
      event.candidateId !== first.candidateId ||
      event.projectId !== first.projectId ||
      event.journalRunId !== first.journalRunId
    ) {
      throw new Error("CANDIDATE_LIFECYCLE_SCOPE_MISMATCH");
    }
    if (transitionIds.has(event.transitionId)) {
      throw new Error("CANDIDATE_LIFECYCLE_TRANSITION_ID_REUSED");
    }
    if (aggregateVersions.has(event.aggregateVersion)) {
      throw new Error("CANDIDATE_LIFECYCLE_AGGREGATE_VERSION_REUSED");
    }
    const expectedVersion = history.length + 1;
    if (event.aggregateVersion !== expectedVersion) {
      throw new Error(
        `CANDIDATE_LIFECYCLE_AGGREGATE_VERSION_GAP:${expectedVersion}:${event.aggregateVersion}`,
      );
    }
    const expectedPreviousTransitionId = history.at(-1)?.transitionId ?? null;
    if (event.previousTransitionId !== expectedPreviousTransitionId) {
      throw new Error("CANDIDATE_LIFECYCLE_PREVIOUS_TRANSITION_MISMATCH");
    }
    const isForkBootstrap =
      history.length === 0 &&
      event.transition === "instantiate" &&
      event.parentSkuLineage !== undefined;
    const expectedFromState = isForkBootstrap ? event.fromState : state;
    if (event.fromState !== expectedFromState) {
      throw new Error(
        `CANDIDATE_LIFECYCLE_STATE_MISMATCH:${state ?? "null"}:${event.fromState ?? "null"}`,
      );
    }
    transitionIds.add(event.transitionId);
    aggregateVersions.add(event.aggregateVersion);
    state = event.toState;
    customerInstanceId = event.customerInstanceId ?? customerInstanceId;
    history.push(structuredClone(event));
  }
  const last = history.at(-1)!;
  return {
    skuId: first!.skuId,
    candidateId: first!.candidateId,
    projectId: first!.projectId,
    journalRunId: first!.journalRunId,
    state: state!,
    aggregateVersion: last.aggregateVersion,
    ...(customerInstanceId ? { customerInstanceId } : {}),
    lastTransitionId: last.transitionId,
    updatedAt: last.occurredAt,
    history,
  };
}

export function validateOutcomeSkuAdmission(input: {
  outcomeSku: unknown;
  lifecycleEvents: CandidateLifecycleEvent[];
}): ProductizationContractValidationResult {
  const localValidation = validateOutcomeSku(input.outcomeSku);
  if (!localValidation.valid) return localValidation;
  const errors: string[] = [];
  let projection: CandidateLifecycleProjection;
  try {
    projection = deriveCandidateLifecycleProjection(input.lifecycleEvents);
  } catch (error) {
    errors.push(
      `lifecycleEvents do not form an authoritative C-2 projection: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
    return { valid: false, errors };
  }
  const sku = input.outcomeSku as OutcomeSku;
  const binding = sku.lifecycleBinding;
  const projectedSkuState: OutcomeSkuState =
    projection.state === "customer-instance" ? "published" : projection.state;
  const comparisons: Array<[string, unknown, unknown]> = [
    ["skuId", sku.skuId, projection.skuId],
    ["state", sku.state, projectedSkuState],
    ["lifecycleBinding.projectId", binding.projectId, projection.projectId],
    [
      "lifecycleBinding.candidateId",
      binding.candidateId,
      projection.candidateId,
    ],
    [
      "lifecycleBinding.journalRunId",
      binding.journalRunId,
      projection.journalRunId,
    ],
    [
      "lifecycleBinding.aggregateVersion",
      binding.aggregateVersion,
      projection.aggregateVersion,
    ],
    [
      "lifecycleBinding.lastTransitionId",
      binding.lastTransitionId,
      projection.lastTransitionId,
    ],
  ];
  for (const [label, actual, expected] of comparisons) {
    if (actual !== expected) {
      errors.push(`${label} does not match the derived C-2 projection`);
    }
  }
  const retirementSourceState =
    projection.state === "retired"
      ? projection.history.at(-1)?.fromState
      : null;
  if (
    retirementSourceState === "verified" ||
    retirementSourceState === "published"
  ) {
    const preservedValidation = validateOutcomeSku({
      ...sku,
      state: retirementSourceState,
    });
    for (const error of preservedValidation.errors) {
      errors.push(
        `retired ${retirementSourceState} SKU must preserve its prior contract: ${error}`,
      );
    }
  }
  const latestVerifyEvent = [...projection.history]
    .reverse()
    .find((event) => event.transition === "verify");
  const lifecycleCapsuleRefs = new Set(
    (latestVerifyEvent?.evidenceRefs ?? [])
      .filter((binding) => binding.role === "verification-capsule")
      .map((binding) => `${binding.ref.ref}\0${binding.ref.contentHash}`),
  );
  for (const binding of sku.evidence.verificationCapsuleRefs) {
    const identity = `${binding.capsuleRef.ref}\0${binding.capsuleRef.contentHash}`;
    if (!lifecycleCapsuleRefs.has(identity)) {
      errors.push(
        "verification capsule coverage is not present in the derived C-2 history",
      );
    }
  }
  const requiresVerifiedEvidenceBinding =
    sku.state === "verified" ||
    sku.state === "published" ||
    retirementSourceState === "verified" ||
    retirementSourceState === "published";
  if (requiresVerifiedEvidenceBinding) {
    const assessmentBinding = latestVerifyEvent?.evidenceRefs.find(
      (binding) => binding.role === "sufficiency-assessment",
    );
    if (
      !assessmentBinding ||
      assessmentBinding.ref.contentHash !==
        sku.evidence.sufficiencyAssessmentRevision
    ) {
      errors.push(
        "sufficiency assessment revision does not match the latest C-2 verify transition",
      );
    }
  }
  return { valid: errors.length === 0, errors };
}

export function assertOutcomeSkuAdmission(input: {
  outcomeSku: unknown;
  lifecycleEvents: CandidateLifecycleEvent[];
}): asserts input is {
  outcomeSku: OutcomeSku;
  lifecycleEvents: CandidateLifecycleEvent[];
} {
  const validation = validateOutcomeSkuAdmission(input);
  if (!validation.valid) {
    throw new Error(
      `INVALID_OUTCOME_SKU_ADMISSION:${validation.errors.join(";")}`,
    );
  }
}
